import React from 'react';

import {Storable} from 'constants/SampleConstants';
import * as FileConstants from 'folder-items/FileConstants';
import {TargetStorable} from 'folder-items/FileConstants';
import StorableSearchInput from 'samples/storables/StorableSearchInput';
import {getRequiredLinkStorableSchema} from 'schemas/fields/SchemaFieldConstants';
import {ChipSize} from 'ui-library/chips/Chip';
import {NoResultOption} from 'ui-library/inputs/ChipSearchInput';
import {ElementKeyboardEvent} from 'util/Types';

export interface StorableLinkInputProps {
  areChipsClickable?: boolean;
  autoFocus: boolean;
  chipSize?: ChipSize;
  defaultValue: FileConstants.SchemaTag;
  onBlur?: () => void;
  onChange: (tagValue: Partial<FileConstants.SchemaTag>) => void;
  onKeyDown?: (e: ElementKeyboardEvent) => void;
  query?: string;
  onUpdateQuery?: (query: string) => void;
  summarize?: boolean;
  noResultsOptions?: Array<NoResultOption> | null;
  placeholder?: string;
  includeArchived: boolean;
  isDisabled?: boolean;
}

export default function StorableLinkInput({
  summarize = false,
  ...props
}: StorableLinkInputProps): JSX.Element {
  const {tagSchemaField, targetStorables, value} = props.defaultValue;
  const storableSchema = getRequiredLinkStorableSchema(tagSchemaField);

  // Similar to other tag input components (e.g., BioentityLinkInput),
  // since all storable links already have a text representation in the 'value' key,
  // we only populate the initial query if there are no links already.
  let initialQuery: string | undefined = '';
  if (targetStorables && targetStorables.length === 0) {
    initialQuery = value ?? undefined;
  }

  return (
    <StorableSearchInput<Storable | TargetStorable>
      disabled={props.isDisabled}
      areChipsClickable={props.areChipsClickable}
      autoFocus={props.autoFocus}
      chipSize={props.chipSize}
      initialQuery={initialQuery}
      onKeyDown={props.onKeyDown}
      onChange={(storables) => {
        props.onChange({
          targetStorables: storables.map((storable) => ({
            id: storable.id,
            name: storable.name,
            // eslint-disable-next-line benchlint/no-in-operator
            iconId: 'storableSchema' in storable ? storable.storableSchema.iconId : storable.iconId,
          })),
          value: '',
        });
      }}
      selectedStorables={targetStorables ?? []}
      shouldAllowMultiple={tagSchemaField.isMulti}
      storableSchemaId={storableSchema?.id}
      summarize={summarize}
      noResultsOptions={props.noResultsOptions}
      // This component can optionally be controlled. See ItemSearchInput for where the magic happens.
      query={props.query}
      onUpdateQuery={props.onUpdateQuery}
      placeholder={props.placeholder}
      includeArchived={props.includeArchived}
    />
  );
}
